import { Delete } from "lucide-react";
import { useEffect } from "react";

import { cn } from "@/lib/utils.ts";

interface Props {
  pin: string;
  onPinChange: (pin: string) => void;
  maxLength?: number;
  disabled?: boolean;
  error?: string | null;
}

const KEYS: Array<{ label: string; value: string }> = [
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
  { label: "4", value: "4" },
  { label: "5", value: "5" },
  { label: "6", value: "6" },
  { label: "7", value: "7" },
  { label: "8", value: "8" },
  { label: "9", value: "9" },
];

export function PinPad({ pin, onPinChange, maxLength = 4, disabled = false, error }: Props) {
  const handleDigit = (d: string) => {
    if (disabled) return;
    if (pin.length >= maxLength) return;
    onPinChange(pin + d);
  };
  const handleBackspace = () => {
    if (disabled) return;
    onPinChange(pin.slice(0, -1));
  };
  const handleClear = () => {
    if (disabled) return;
    onPinChange("");
  };

  // Soporte de teclado físico para desktop dev.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.key >= "0" && e.key <= "9") handleDigit(e.key);
      else if (e.key === "Backspace") handleBackspace();
      else if (e.key === "Escape") handleClear();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, disabled]);

  return (
    <div className="flex flex-col items-center gap-6">
      {/* PIN dots */}
      <div className="flex gap-3">
        {Array.from({ length: maxLength }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-4 w-4 rounded-full border-2 transition-colors",
              i < pin.length ? "border-slate-900 bg-slate-900" : "border-slate-300 bg-white",
              error && "border-red-500",
            )}
          />
        ))}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Keypad 3x4 */}
      <div className="grid grid-cols-3 gap-3">
        {KEYS.map((k) => (
          <button
            key={k.value}
            type="button"
            onClick={() => handleDigit(k.value)}
            disabled={disabled}
            className="touch-target h-16 w-16 rounded-xl bg-white text-2xl font-medium text-slate-900 shadow-sm hover:bg-slate-50 active:bg-slate-100 disabled:opacity-50"
          >
            {k.label}
          </button>
        ))}
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className="touch-target h-16 w-16 rounded-xl bg-slate-100 text-sm font-medium text-slate-700 hover:bg-slate-200 active:bg-slate-300 disabled:opacity-50"
        >
          Limpiar
        </button>
        <button
          type="button"
          onClick={() => handleDigit("0")}
          disabled={disabled}
          className="touch-target h-16 w-16 rounded-xl bg-white text-2xl font-medium text-slate-900 shadow-sm hover:bg-slate-50 active:bg-slate-100 disabled:opacity-50"
        >
          0
        </button>
        <button
          type="button"
          onClick={handleBackspace}
          disabled={disabled}
          aria-label="Borrar último dígito"
          className="touch-target flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 disabled:opacity-50"
        >
          <Delete className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
